import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { ContactGuestSchema } from '../../definitions/schemas';
import { Events } from '../../enums/Analytics';
import { handleAutofocus } from '../../lib/TextField';
import { OrganizationFlagEnum } from '../../lib/features/FeatureFlag';
import { trackContactSaved } from './analytics';
import { useI18n } from '../../lib/hook/useI18n';
import ContactsInterface from '../../graphql/Contacts/ContactsInterface';
import PrivacyPolicyLink from '../ConsentPromp/PrivacyPolicyLink';
import SnackbarStore from '../../stores/observables/SnackbarStore';
import ThemableButton from '../../theme/components/Button/ThemableButton';
import ThemableTextField from '../../theme/components/TextField/ThemableTextField';
import useFeatureFlag from '../../lib/hook/useFeatureFlag';

interface Props {
  contact: ContactGuestSchema;
  modalZIndex?: number;
  onClose: () => void;
  onSave: (guest: ContactGuestSchema) => void;
  open: boolean;
  referrer: string;
  roomId: string;
}

const ContactNameEditor: React.FC<Props> = (props: Props): JSX.Element => {
  const { i18nClasses, t, tc } = useI18n();

  const isInhouseConsentEnabled = useFeatureFlag(
    OrganizationFlagEnum.INHOUSE_CONSENT
  );

  const { contact, modalZIndex, onClose, onSave, open, referrer, roomId } =
    props;

  const { firstName: initialFirstName, lastName: initialLastName } = contact;

  const [firstName, setFirstName] = useState(initialFirstName || '');
  const [lastName, setLastName] = useState(initialLastName || '');

  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const { firstName: initialFirstName, lastName: initialLastName } = contact;
    setFirstName(initialFirstName || '');
    setLastName(initialLastName || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact, open]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      setSaving(true);

      const response = await new ContactsInterface(contact.id).update({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        roomId,
      });

      SnackbarStore.show('Contact name edited successfully!');

      trackContactSaved(contact, Events.CONTACT_NAME_EDITED, referrer);
      onSave(response.updateContact.contact);
    } catch (e) {
      SnackbarStore.show(`${e}`);
    } finally {
      setSaving(false);
    }
  };

  const inputChangeHandler =
    (setter: typeof setFirstName | typeof setLastName) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setter(e.target.value);
      setDirty(true);
    };

  return (
    <Dialog
      onClose={onClose}
      open={open}
      disablePortal={!modalZIndex}
      slotProps={
        modalZIndex != null
          ? { root: { style: { zIndex: modalZIndex } } }
          : undefined
      }
    >
      <form onSubmit={onSubmit} tabIndex={-1}>
        <DialogTitle>{t('edit_name.modal.title')}</DialogTitle>
        <DialogContent>
          <Box pt={1}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <ThemableTextField
                  fullWidth
                  inputRef={handleAutofocus(!firstName, dirty)}
                  label={t('edit_name.modal.first_name')}
                  onChange={inputChangeHandler(setFirstName)}
                  value={firstName}
                />
              </Grid>
              <Grid item xs={12}>
                <ThemableTextField
                  fullWidth
                  inputRef={handleAutofocus(!!firstName && !lastName, dirty)}
                  label={t('edit_name.modal.last_name')}
                  onChange={inputChangeHandler(setLastName)}
                  value={lastName}
                />
              </Grid>
              {isInhouseConsentEnabled && (
                <Grid item xs={12}>
                  <Typography
                    className={i18nClasses.root}
                    color="textSecondary"
                    variant="caption"
                  >
                    {tc(
                      'edit_name.modal.consent',
                      {},
                      {
                        privacyPolicyLink: (
                          <PrivacyPolicyLink variant="caption" />
                        ),
                      },
                      true
                    )}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={onClose}>
            {t('edit_name.modal.cancel')}
          </Button>
          <ThemableButton
            disabled={saving}
            onClick={onSubmit}
            type="submit"
            variant="contained"
          >
            {t('edit_name.modal.save')}
          </ThemableButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ContactNameEditor;
